import { z } from 'zod';
import { MODULE } from './rechte';
import { Kuerzel } from './auth';
import { BrancheSchema } from './branchen';

export const MandantStatus = z.enum(['aktiv', 'eingeladen', 'gesperrt']);

export const MandantZeile = z.object({
  id: z.string().uuid(),
  kuerzel: z.string(),
  name: z.string(),
  status: MandantStatus,
  lizenz: z.enum(['standard', 'whitelabel']),
  module: z.array(z.enum(MODULE)),
  branche: BrancheSchema,
  erstelltAm: z.string(),
  /** dxl = von DXL angelegt, registrierung = selbst registriert, app = über die Shopify-App „DXL Retail Media“ */
  herkunft: z.enum(['dxl', 'registrierung', 'app']),
  /** gebuchtes bzw. zugeordnetes Paket, null = noch keins */
  paket: z.string().nullable(),
  /** Abo über Stripe von DXL, null = keins */
  abo: z.enum(['unvollstaendig', 'testphase', 'aktiv', 'ueberfaellig', 'gekuendigt', 'beendet']).nullable(),
});
export type MandantZeile = z.infer<typeof MandantZeile>;

export const MandantenZaehler = z.object({
  alle: z.number().int(),
  aktiv: z.number().int(),
  eingeladen: z.number().int(),
  gesperrt: z.number().int(),
});
export type MandantenZaehler = z.infer<typeof MandantenZaehler>;

export const MandantenSeite = z.object({
  eintraege: z.array(MandantZeile),
  weiter: z.string().nullable(),
  /** Treffer für Suche + Filter */
  gesamt: z.number().int(),
  /** Anzahl je Status über alle Marktplätze (unabhängig von Suche/Filter) */
  zaehler: MandantenZaehler,
});
export type MandantenSeite = z.infer<typeof MandantenSeite>;

export const MandantenAbfrage = z.object({
  q: z.string().trim().max(100).optional(),
  status: MandantStatus.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const MandantAnlegenSchema = z.object({
  kuerzel: Kuerzel,
  name: z.string().trim().min(2, 'Bitte Namen angeben').max(120),
  lizenz: z.enum(['standard', 'whitelabel']).default('standard'),
  module: z.array(z.enum(MODULE)).min(1, 'Mindestens ein Modul wählen'),
  /** Branche – legt die Vorlage der Produktfelder an */
  branche: BrancheSchema.default('allgemein'),
  /** optional: Inhaber gleich einladen */
  inhaber: z
    .object({
      name: z.string().trim().min(2, 'Bitte Namen angeben').max(120),
      email: z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben'),
    })
    .nullable()
    .default(null),
});
export type MandantAnlegen = z.infer<typeof MandantAnlegenSchema>;

export const MandantDetail = MandantZeile.extend({
  geaendertAm: z.string(),
  nutzer: z.number().int(),
  inhaber: z.object({ name: z.string(), email: z.string(), einladungOffen: z.boolean() }).nullable(),
});
export type MandantDetail = z.infer<typeof MandantDetail>;

export const MandantStatusAendern = z
  .object({
    status: z.enum(['aktiv', 'gesperrt']).optional(),
    /** Branche wechseln – wendet die Vorlage der Produktfelder an */
    branche: BrancheSchema.optional(),
    name: z.string().trim().min(2, 'Bitte Namen angeben').max(120).optional(),
    lizenz: z.enum(['standard', 'whitelabel']).optional(),
    module: z.array(z.enum(MODULE)).min(1, 'Mindestens ein Modul wählen').optional(),
  })
  .refine((d) => Object.values(d).some((v) => v !== undefined), 'Bitte eine Änderung angeben');

/** Stammdaten des Marktplatzes im Plattform-Admin bearbeiten (Kürzel bleibt fest: Adressen und App hängen daran) */
export const MandantBearbeitenSchema = z.object({
  name: z.string().trim().min(2, 'Bitte Namen angeben').max(120),
  lizenz: z.enum(['standard', 'whitelabel']),
  module: z.array(z.enum(MODULE)).min(1, 'Mindestens ein Modul wählen'),
  branche: BrancheSchema,
});
export type MandantBearbeiten = z.infer<typeof MandantBearbeitenSchema>;
